/**
 * Visual variants for the node card. Each variant tints the header icon-chip
 * (and the per-variant `--node-accent` triplet that downstream slots — ports,
 * row-pills, drop-target outline — read off the card root). Maps to the
 * design handoff's `.ns-node--start / --task / --gate / --output` modifiers.
 *
 * `start` is the canonical green source-node accent. `input` is preserved as
 * the original (lavender) variant name for backwards-compat, but renders
 * with the same green triplet as `start` per the redesign.
 */
export const NODE_CARD_VARIANT_NEUTRAL = 'neutral';
export const NODE_CARD_VARIANT_INPUT = 'input';
export const NODE_CARD_VARIANT_START = 'start';
export const NODE_CARD_VARIANT_TASK = 'task';
export const NODE_CARD_VARIANT_GATE = 'gate';
export const NODE_CARD_VARIANT_OUTPUT = 'output';
export const NODE_CARD_VARIANT_GENERATION = 'generation';
export const NODE_CARD_VARIANT_NOTE = 'note';

export type NodeCardVariant =
  | typeof NODE_CARD_VARIANT_NEUTRAL
  | typeof NODE_CARD_VARIANT_INPUT
  | typeof NODE_CARD_VARIANT_START
  | typeof NODE_CARD_VARIANT_TASK
  | typeof NODE_CARD_VARIANT_GATE
  | typeof NODE_CARD_VARIANT_OUTPUT
  | typeof NODE_CARD_VARIANT_GENERATION
  | typeof NODE_CARD_VARIANT_NOTE;

/**
 * Composable runtime states. The shell renders the corresponding ring/halo
 * for each.
 *
 * Semantic split between {@link NODE_CARD_STATE_INVALID} and
 * {@link NODE_CARD_STATE_ORPHAN} mirrors the handoff: invalid (red) is a
 * config-level error inside the node, orphan (yellow) is a wiring oversight
 * (input/output port not connected). Two distinct affordances so the user
 * can tell at a glance whether they need to edit the node or wire it up.
 *
 * `runDone` is distinct from `completed` so the redesign can give the
 * fresh "this just finished" green ring without disturbing existing
 * consumers that read `completed` as a long-lived "last successful run"
 * tint.
 */
export const NODE_CARD_STATE_DEFAULT = 'default';
export const NODE_CARD_STATE_SELECTED = 'selected';
export const NODE_CARD_STATE_RUNNING = 'running';
export const NODE_CARD_STATE_COMPLETED = 'completed';
export const NODE_CARD_STATE_FAILED = 'failed';
export const NODE_CARD_STATE_INVALID = 'invalid';
export const NODE_CARD_STATE_ORPHAN = 'orphan';
export const NODE_CARD_STATE_RUNDONE = 'runDone';
// Kind-specific resting halos for the first / last node in the chain.
// The design uses these to whisper "this is where the chain begins/ends"
// without competing with the noisier run / warning rings, so the
// underlying outline stays the regular resting stroke.
export const NODE_CARD_STATE_SOURCE = 'source';
export const NODE_CARD_STATE_TERMINAL = 'terminal';

export type NodeCardState =
  | typeof NODE_CARD_STATE_DEFAULT
  | typeof NODE_CARD_STATE_SELECTED
  | typeof NODE_CARD_STATE_RUNNING
  | typeof NODE_CARD_STATE_COMPLETED
  | typeof NODE_CARD_STATE_FAILED
  | typeof NODE_CARD_STATE_INVALID
  | typeof NODE_CARD_STATE_ORPHAN
  | typeof NODE_CARD_STATE_RUNDONE
  | typeof NODE_CARD_STATE_SOURCE
  | typeof NODE_CARD_STATE_TERMINAL;

/**
 * Editor mode toggle. Drives the dashed-vs-solid outline treatment from the
 * handoff: edit mode shows a 1.5px dashed primary 50% mix outline so authors
 * can tell at a glance which node they're authoring; use mode renders the
 * standard outline + a gentler bevel.
 *
 * When {@link NodeCardShellProps.published} is `true`, the dashed outline is
 * suppressed — even in edit mode the published card reads as a solid 2px
 * stroke (it's intentionally locked).
 */
export const NODE_CARD_EDIT_MODE_EDIT = 'edit';
export const NODE_CARD_EDIT_MODE_USE = 'use';

export type NodeCardEditMode =
  | typeof NODE_CARD_EDIT_MODE_EDIT
  | typeof NODE_CARD_EDIT_MODE_USE;

export const NODE_CARD_EMPHASIS_HIGHLIGHTED = 'highlighted';
export const NODE_CARD_EMPHASIS_DIMMED = 'dimmed';

export type NodeCardEmphasis =
  | typeof NODE_CARD_EMPHASIS_HIGHLIGHTED
  | typeof NODE_CARD_EMPHASIS_DIMMED;

export const NODE_CARD_DIMMED_OPACITY = 0.4;

/**
 * Per-variant accent triplet projected onto the card root as
 * `--node-card-accent / --node-card-accent-strong / --node-card-accent-ink`.
 *
 * Slots inside the card body (ports, row-pills, drop-target outline) can
 * read these CSS custom properties with `var(...)` and stay in lockstep
 * with the card's variant without re-declaring the palette per slot.
 *
 * Hex values come straight from the design handoff
 * (`styles.css` `.ns-node--{start,gate,output}` blocks). The fallback
 * triplet is the lavender primary cluster the rest of the app uses.
 */
export interface NodeAccentTriplet {
  /** Pale fill — used for resting tints, dotted-port background. */
  accent: string;
  /** Saturated stroke — used for drop-target outline, port border. */
  strong: string;
  /** Dark ink — used for text/icons rendered on top of `accent`. */
  ink: string;
}

export const NODE_CARD_ACCENTS: Record<NodeCardVariant, NodeAccentTriplet> = {
  [NODE_CARD_VARIANT_NEUTRAL]: {
    accent: '#CFC9F5',
    strong: '#9892EE',
    ink: '#1E1A8A',
  },
  [NODE_CARD_VARIANT_INPUT]: {
    accent: '#C7E9B7',
    strong: '#8CD88C',
    ink: '#1F4720',
  },
  [NODE_CARD_VARIANT_START]: {
    accent: '#C7E9B7',
    strong: '#8CD88C',
    ink: '#1F4720',
  },
  [NODE_CARD_VARIANT_TASK]: {
    accent: '#CFC9F5',
    strong: '#9892EE',
    ink: '#1E1A8A',
  },
  [NODE_CARD_VARIANT_GATE]: {
    accent: '#F4E1A4',
    strong: '#E8C268',
    ink: '#4A3300',
  },
  [NODE_CARD_VARIANT_OUTPUT]: {
    accent: '#F4E1A4',
    strong: '#F4E1A4',
    ink: '#4A3300',
  },
  [NODE_CARD_VARIANT_GENERATION]: {
    accent: '#FFA9FC',
    strong: '#FF80F8',
    ink: '#590060',
  },
  [NODE_CARD_VARIANT_NOTE]: {
    accent: '#F4E1A4',
    strong: '#E8C268',
    ink: '#4A3300',
  },
};
